import { defineStore } from 'pinia';
import { i18n } from '../../../../i18n';
import { useAuthUserStore } from '@/app/stores/user';
import { WEB_LINKS } from '@/shared/web/type';
import { router } from '@/app/providers/router';
import { DOCUMENT_TAB } from '@/pages/documents/lib';
import { manyStatement, NotificationState, NotificationStatus } from './types';
import { Origin } from '@/app/graphql/notification/sdk.notification';
import {
  isNotificationPaymentService,
  isNotificationStatementService,
  NotificationLineWebSocketMessage,
  NotificationPaymentMessage,
  NotificationStatement,
  NotificationStatementMessage,
  readAllMessage,
  readMessage,
  findAllMessages,
  deleteAllMessage,
  deleteMessage,
  deleteMultipleMessage,
} from '@/shared/services/notification';
import dayjs from 'dayjs';

export const useNotificationStore = defineStore('notification', {
  state: (): NotificationState => ({
    notifications: [],
    isAllowPushNotifications: false,
  }),
  getters: {
    hasUnreadNotification: (state) =>
      state.notifications.some((notification) => notification.status === NotificationStatus.SENT),
    hasNotification: (state) => !!state.notifications.length,
    hasServiceNotification: (state) =>
      state.notifications.some((notification) => notification.origin !== Origin.Manual),
    getStatementNotifications: (state) => state.notifications.filter((message) => message.origin === Origin.Statement),
    notificationMessages: (state) => {
      const { userContracts } = useAuthUserStore();
      const StatementNotification = state.notifications.filter(
        (notification) => notification.origin === Origin.Statement
      );
      const allNotification = state.notifications.filter((notification) => notification.origin !== Origin.Statement);
      if (userContracts) {
        userContracts.forEach((contract) => {
          const countStatement = StatementNotification.filter(
            (notificationId) =>
              (notificationId.payload as NotificationStatement)?.legalPartyId === contract.orgReferenceId
          );
          if (countStatement.length > 1) {
            allNotification.unshift({
              ...countStatement[0],
              legalPartyName: contract.companyName,
              text: manyStatement.text,
              title: manyStatement.title,
              classList: 'text-grey-7',
              countStatement: countStatement.length,
              idGroup: countStatement.map((statement) => statement.id),
            });
          }
          if (countStatement.length === 1) {
            allNotification.unshift({
              ...countStatement[0],
              legalPartyName: contract.companyName,
              idGroup: [countStatement[0].id],
            });
          }
        });
        return allNotification.sort((a, b) => dayjs(b.dateTime).unix() - dayjs(a.dateTime).unix());
      }
    },
  },
  actions: {
    reset() {
      this.$reset();
    },
    selectContract(legalPartyId: number) {
      const { setUserSelectedContract, userContracts, selectedUserContract } = useAuthUserStore();
      const contract = userContracts?.find((id) => id.orgReferenceId === legalPartyId) || null;
      if (contract && selectedUserContract !== contract) setUserSelectedContract(contract);
    },
    async getAllNotifications() {
      try {
        this.notifications = await findAllMessages();
      } catch (e: any) {
        throw { ...e };
      }
    },
    async deleteAllNotifications() {
      try {
        await deleteAllMessage();
        await this.getAllNotifications();
      } catch (e: any) {
        throw { ...e };
      }
    },
    async addNotifications(message: NotificationLineWebSocketMessage) {
      const { selectedUserContract } = useAuthUserStore();
      if (
        isNotificationStatementService(message) &&
        router.currentRoute.value.name === WEB_LINKS.DOCUMENTS &&
        router.currentRoute.value.params.name === DOCUMENT_TAB.STATEMENTS &&
        message.payload.legalPartyId === selectedUserContract?.orgReferenceId
      ) {
        await deleteMessage(message.id);
      } else {
        this.notifications.unshift(message);
      }
    },
    async readNotification(notificationId: number) {
      try {
        await readMessage(notificationId);
        this.setReadStatus(notificationId);
      } catch (e: any) {
        throw { ...e };
      }
    },
    async deleteMultipleNotification(notificationId: number[]) {
      try {
        await deleteMultipleMessage(notificationId);
        this.notifications = this.notifications.filter((message) => !notificationId.find((id) => id === message.id));
      } catch (e: any) {
        throw { ...e };
      }
    },
    async deleteNotification(notificationId: number) {
      try {
        await deleteMessage(notificationId);
        this.notifications = this.notifications.filter((message) => message.id !== notificationId);
      } catch (e: any) {
        throw { ...e };
      }
    },
    async readAll() {
      try {
        if (!this.hasNotification) return;
        await readAllMessage();
        this.setReadStatus();
      } catch (e: any) {
        throw { ...e };
      }
    },
    setPushNotifications(status: boolean) {
      this.isAllowPushNotifications = status;
    },
    setReadStatus(notificationId?: number) {
      if (typeof notificationId === 'number') {
        this.notifications = this.notifications.map((notification) => {
          if (notification.id === notificationId) {
            return {
              ...notification,
              status: NotificationStatus.READ,
            };
          }
          return notification;
        });
        return;
      }
      this.notifications = this.notifications.map((notification) => ({
        ...notification,
        status: NotificationStatus.READ,
      }));
    },
    getCurrentContractStatementNotification(legalPartyId: number) {
      return this.notifications.filter(
        (notification) =>
          notification.origin === Origin.Statement &&
          (notification.payload as NotificationStatement)?.legalPartyId === legalPartyId
      );
    },

    async deleteMultipleStatementMessage(legalPartyId?: number) {
      try {
        const { selectedUserContract } = useAuthUserStore();
        const id = legalPartyId ?? selectedUserContract?.orgReferenceId;
        if (!id) return;

        const notificationIdList = this.getCurrentContractStatementNotification(id).map(
          (notification) => notification.id
        );
        if (!notificationIdList.length) return;
        await this.deleteMultipleNotification(notificationIdList);
      } catch (e: any) {
        throw { ...e };
      }
    },
    showNotification(message: NotificationLineWebSocketMessage) {
      if (document.visibilityState !== 'hidden') return;
      const title =
        isNotificationStatementService(message) || isNotificationPaymentService(message)
          ? i18n.t(message.title)
          : message.title;
      const body =
        message.text && (isNotificationStatementService(message) || isNotificationPaymentService(message))
          ? i18n.t(message.text)
          : (message.text ?? '');

      const notification = new Notification(title, { body });
      notification.onclick = function () {
        window.parent.focus();
        notification.close();
      };
    },
    async redirectDocumentStatement(notification: NotificationStatementMessage) {
      try {
        this.selectContract(notification.payload.legalPartyId);
        await this.deleteMultipleNotification(notification.idGroup);
        await router.push({ name: WEB_LINKS.DOCUMENTS, params: { name: 'statements' } });
      } catch (e: any) {
        throw { ...e };
      }
    },
    async redirectHistoryPayment(notification: NotificationPaymentMessage) {
      try {
        this.selectContract(notification.payload.legalPartyId);
        await this.deleteNotification(notification.id);
        await router.push({
          name: WEB_LINKS.PAYMENT_DETAIL,
          params: { id: notification.payload.paymentId, operationCode: notification.payload.operationCode },
        });
      } catch (e: any) {
        throw { ...e };
      }
    },
    async requestAndShowPermission(message: NotificationLineWebSocketMessage) {
      await Notification.requestPermission((permission) => {
        if (permission === 'granted') this.showNotification(message);
      });
    },
  },
});
